import React, { useContext, useEffect, useRef, useState } from "react";
import { Modal, Box, Grid, IconButton, Typography, Button } from "@mui/material";
import { ModalContainer } from "./styles";
import { StartSubscription } from "./StartSubscription";
import { CancelSubscription } from "./CancelSubscription";
import { CreateTeamWizard } from "../team/create";
import { useMattoState } from "../MattoState";
import { getStripeInvoicesAPIUrl, getSubscriptionAPIUrl  } from "./config.js";
import { getContactForm } from "../libs/util.js";
import { fetchBilling } from "./billingRefusal";
import { formatPrice, useActiveBalance, useCreditWorkspace } from "ui";
import { useLoginState } from "../studioxr/auth/LoginState.js";
import { ProfileInterface, UserProfileRole } from "ts-interfaces";
import AuthModal from "../studioxr/auth/AuthModal";
import { Close as CloseIcon } from "@mui/icons-material";
import { Stack } from "@mui/system";
import { styled } from '@mui/system';
import { ActionButton } from "./../buttons/ButtonStyles";
import { revertStripeSubscriptionAPIUrl } from "./config.js";
import { BillingRecoveryPanel, isBillingRecoveryState } from "./BillingRecovery";
import { PausedPanel } from "./PauseMembership";
import { DeleteAccountSection } from "../studioxr/auth/DeleteAccount/DeleteAccountSection";


const CancelBtn = styled(Button)(({ theme }:any) => ({ background: '#C68CD6', color:'white', border: '1px solid #C68CD6', marginRight:'20px', padding: '8px 16px', fontWeight: 'bold', fontSize: '12px',
                                                  maxHeight:theme.subBarButtons.maxHeight,'&:hover':{'color':'#C68CD6','backgroundColor':'white'}}));

const ChangeBillingBtn = styled(Button)(({ theme }:any) => ({ background: '#C68CD6', color:'white', border: '1px solid #C68CD6', marginRight:'20px', padding: '8px 16px', fontWeight: 'bold', fontSize: '12px',
                                                    maxHeight:theme.subBarButtons.maxHeight,'&:hover':{'color':'#C68CD6','backgroundColor':'white'}}));
  
const StyledModal = ({ children, containerSx = undefined, ...rest }) => {
  return (
    <Modal {...rest}>
      <ModalContainer sx={containerSx}>
        <Box className="inner-root">
          {children}
        </Box>
      </ModalContainer>
    </Modal>
  );
};

// Step 0 of the checkout (the plan overview) needs a wider shell than the
// split-pane steps: 1140 on desktop, a full-screen sheet on phones.
const planOverviewShellSx = {
  width: { xs: "100%", sm: "calc(100vw - 48px)" },
  maxWidth: { xs: "none", sm: "calc(100vw - 48px)", lg: "1140px" },
  height: { xs: "100%", sm: "auto" },
  minHeight: { xs: "100%", sm: "auto" },
  maxHeight: { xs: "100vh", sm: "calc(100vh - 80px)" },
  borderRadius: { xs: 0, sm: "18px", lg: "20px" },
};

const UserSubscriptionModal = () => {
  const profile:ProfileInterface | any = useLoginState((state) => state.profile);
  const showSubscription = useMattoState((state) => state.showSubscription);
  const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  const [checkoutStep, setCheckoutStep] = useState<number | null>(null);
  // The plan overview is the only in-modal route into the team wizard, so the
  // checkout value that was showing before 'createTeam' (entry kind and any
  // feature-gate message) is what the wizard's first Back returns to.
  const planOverviewEntry = useRef<any>(null);
  // Back from the wizard: reopen on the overview, and don't count the modal view again.
  const [backFromWizard, setBackFromWizard] = useState(false);
  useEffect(() => { if (!showSubscription) setBackFromWizard(false); }, [showSubscription]);

  useEffect(() => {
    if ( !profile?.stripeCustomerId) { return; }
    const urlParams = new URLSearchParams(window.location.search);    
    if (urlParams.get('accounts') == 'true') { setShowSubscription('accounts'); }
  // Once per customer, not on every profile change: pausing or cancelling
  // updates the profile, and re-opening Accounts here would replace their
  // confirmation screens for anyone who arrived on ?accounts=true.
  },[profile?.stripeCustomerId])


  const handleClose = (event, reason) => {  setShowSubscription(null); }

  const sub = Array.isArray(showSubscription) ? showSubscription[0] : showSubscription;
  const wantsCheckout = sub == "startTrial" || sub == "choosePlan";
  if (sub !== "createTeam") planOverviewEntry.current = wantsCheckout ? showSubscription : null;
  // A paused member (issue #3957) is basic but still subscribed, so checkout
  // would be refused: every upgrade entry opens the account panel, which
  // offers Resume, instead.
  const paused = profile?.billingState === "paused";
  // Stored, not only rendered: Resume clears billingState, and the modal
  // would otherwise fall back to the checkout that was asked for.
  useEffect(() => {
    if (wantsCheckout && paused) setShowSubscription("accounts");
  }, [wantsCheckout, paused]);
  // "choosePlan" skips the plan overview and opens on the plan picker (but
  // Back from the team wizard always lands on the overview).
  if (wantsCheckout && !paused) {
      return (
        <StyledModal open={true} onClose={handleClose} containerSx={checkoutStep === 0 ? planOverviewShellSx : undefined}>
          <StartSubscription
            initialStep={sub == "choosePlan" && !backFromWizard ? 1 : 0}
            leaveForLibrary={sub == "choosePlan"}
            trackView={!backFromWizard}
            onStepChange={setCheckoutStep} />
        </StyledModal>
      );
    }
    else if (sub == "accounts" || wantsCheckout) {
      return <AccountsModal />
    }
    else if (sub == "cancel") {
      return (
        <StyledModal open={true} onClose={handleClose} containerSx={{ maxWidth: { xs: "500px", md: "640px" } }}>
          <CancelSubscription/>
        </StyledModal>
      );
    }
    else if (sub == "createTeam") {
      const backTo = planOverviewEntry.current;
      return (
        <CreateTeamModalHost
          onClose={() => setShowSubscription(null)}
          onBack={backTo ? () => { setBackFromWizard(true); setShowSubscription(backTo); } : undefined}
        />
      );
    } else return null;
};

export default UserSubscriptionModal;

// Two columns from md (value panel + form), one column on a full-screen sheet on phones.
// A fixed height (fits the details screen) so moving to payment never resizes the modal.
const createTeamShellSx = {
  width: { xs: "100%", sm: "calc(100vw - 48px)" },
  maxWidth: { xs: "none", sm: "640px", md: "976px", lg: "1036px" },
  height: { xs: "100%", sm: "min(740px, calc(100vh - 48px))" },
  minHeight: { xs: "100%", sm: "auto" },
  // dvh: iOS Safari's 100vh is taller than the visible area while its toolbar shows.
  maxHeight: { xs: "100vh", sm: "calc(100dvh - 48px)" },
  borderRadius: { xs: 0, sm: "18px", lg: "20px" },
};

// Same modal shell as the personal payment flow. While the wizard reports
// busy (payment in flight or already submitted), backdrop click / Esc must NOT dismiss it — unmounting
// the wizard would destroy its double-charge latch, and a reopened wizard
// could submit a second charge. The wizard's own buttons stay the only way
// out in that state.
const CreateTeamModalHost = ({ onClose, onBack }: { onClose: () => void; onBack?: () => void }) => {
  const [locked, setLocked] = useState<boolean>(false);
  const handleShellClose = (
    _event: {},
    _reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (locked) return;
    onClose();
  };
  return (
    <Modal open={true} onClose={handleShellClose} disableEscapeKeyDown={locked}>
      <ModalContainer sx={createTeamShellSx}>
        <Box className="inner-root">
          <CreateTeamWizard onClose={onClose} onBack={onBack} onBusyChange={setLocked} />
        </Box>
      </ModalContainer>
    </Modal>
  );
};

const AccountsModal = () => {
  const profile:ProfileInterface | any = useLoginState((state) => state.profile);
  const permissions = useLoginState((state) => state.permissions);
  const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  // Credit context follows the ACTIVE workspace (the team wallet for team
  // designers when workspaces are gated in prod) — NOT membership-wide. So in
  // the Personal workspace this shows personal credits / "Free Plan", not
  // the team's.
  const creditWorkspace = useCreditWorkspace(profile?.uid ?? null, permissions);
  const isTeamWorkspace = creditWorkspace.kind === "team";
  const { balance: effectiveBalance } = useActiveBalance(creditWorkspace);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [customerUpcomingInvoice, setCustomerUpcomingInvoice] = useState<any>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<any>(null);
  const [planLoadFailed, setPlanLoadFailed] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState('');
  const [subscriptionAmount, setSubscriptionAmount] = useState(0);

  const handleClose = (event, reason) => { setShowSubscription(null); }
  // While paused the upcoming invoice is one Stripe will void, so the
  // "next payment" and renewal lines are hidden.
  const isPaused = Boolean(subscriptionPlan?.subscription?.pause_collection);

  useEffect(()=> { fetchCustomerInvoices(); },[profile?.stripeCustomerId])

  async function revertStripeSubscription() {
    const requestRevert = { customerId: profile?.stripeCustomerId };
    return fetchBilling("revertStripeSubscription", requestRevert, revertStripeSubscriptionAPIUrl);
  }

  const revertSubscription = async () => { 
    try {
      const revertedStripeSubscriptionPlan  = await revertStripeSubscription();
      // fetchBilling returns undefined when the request fails.
      if (!revertedStripeSubscriptionPlan) throw new Error("Revert was not confirmed");
      setSubscriptionPlan(revertedStripeSubscriptionPlan);
      fetchCustomerInvoices();
    }
    catch (error) {
      alert("There is some error in reverting your subscription. Please contact studio@mattoboard.com for assistance.")
    }
  }

  const subscriptionPlanView = () => {

    if(!profile?.stripeCustomerId) {
      if (isTeamWorkspace) return <Typography>Mattoboard Pro Teams</Typography>
      return <Typography>Free Plan</Typography>
    }
    // Paused (issue #3957): Stripe keeps the status "active", so this comes
    // before the generic Pro panel. The live subscription decides.
    else if (isPaused) {
      return <PausedPanel resumesAt={subscriptionPlan.subscription.pause_collection.resumes_at ?? profile?.billingResumesAt} onResumed={fetchCustomerInvoices} />
    }
    else if (subscriptionPlan?.subscription?.cancel_at_period_end==true) {
      return ( 
        <Stack direction="column" spacing={1}>
          <Typography variant="h5" component="h5">Your Subscription <Typography component="span" variant="h5" sx={{color: '#cc010c', fontWeight: 'bold'}}>will be canceled</Typography></Typography>
          <Typography sx={{paddingTop: '20px', fontWeight: 'bold'}}>Cancel Date: {subscriptionEndDate}</Typography>
          <Typography sx={{paddingBottom: '10px'}}>You will be able to use Mattoboard Pro until your cancel date.  If you change your mind and want to resume Mattoboard Pro click below.  Come back and cancel anytime</Typography>
          <ActionButton sx={{fontWeight: 'bold', width: '80%', marginLeft: '10%!important'}} onClick={revertSubscription}>KEEP MY SUBSCRIPTION</ActionButton>
        </Stack>
      );
    }
    // Dunning (issue #3956): a past_due subscriber gets their billing state
    // and the two recovery actions, not the generic "Cancel" panel.
    // Stripe's live status decides (the profile flag can lag the webhook by
    // a minute); the next attempt date comes from the profile stamp.
    else if (isBillingRecoveryState(subscriptionPlan?.subscription?.status)) {
      return <BillingRecoveryPanel state={subscriptionPlan.subscription.status} nextAttemptAt={profile?.billingNextAttemptAt} />
    }
    else if(subscriptionPlan?.subscription?.status == 'active' || subscriptionPlan?.subscription?.status == 'trialing' || subscriptionPlan?.subscription?.status == 'incomplete') {
      return ( 
        <Grid sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          justifyContent: { xs: 'flex-start', md: 'space-between' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 2, sm: 2, md: 0 },
          marginRight: { xs: 0, md: '20px' }
        }}>
          <Typography sx={{ marginBottom: { xs: 1, sm: 1, md: 0 } }}>
            Mattoboard PRO Tier.
          </Typography>
          <CancelBtn 
            onClick={() => setShowSubscription('cancel')}
            sx={{ 
              alignSelf: { xs: 'stretch', sm: 'stretch', md: 'auto' }, // Full width on mobile, auto on desktop
              maxWidth: { xs: '100%', sm: '200px', md: 'auto' } // Limit width on sm screens
            }}
          >
            Cancel Subscription
          </CancelBtn>
        </Grid>

      )
    } else if(subscriptionPlan && !subscriptionPlan.subscription) {
      return <Typography>Free Plan</Typography>
    } else if (planLoadFailed) {
      return <Typography role="alert">We couldn't load your subscription. Please try again or contact studio@mattoboard.com.</Typography>
    } else {
      return <Typography>Loading Plan...</Typography>
    }
  }

  const needMoreTime = () => {
    const today = new Date().getTime()
    const eightDays = (8 * 24 * 60 * 60 * 1000)

    if (subscriptionPlan?.subscription?.status=='active' 
    && typeof(subscriptionPlan?.subscription?.trial_end)=='number'
    && (subscriptionPlan?.subscription?.trial_end*1000 + eightDays) > today) {  
        return <Typography>Did you need a longer trial?  <a target='_' href={getContactForm(profile)}>Contact Us For a Full Refund.</a> </Typography>
    }
  }  

const calculateInvoiceDetails = (customerInvoices) => {
    if(customerInvoices?.length > 0) {
            const latestInvoice: any = customerInvoices[0];
            const perioEndDate = new Date(latestInvoice?.lines?.data[0]?.period?.end * 1000); 
            const dueDate = perioEndDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            const subscriptionAmountComputed = (latestInvoice?.lines?.data[0]?.plan?.amount / 100)    
            setSubscriptionEndDate(dueDate)   
            setSubscriptionAmount(subscriptionAmountComputed)
    }
  }

  const nextBill = () => {
    if (subscriptionPlan?.subscription?.cancel_at_period_end==true) {
      const endingDate = subscriptionPlan?.subscription.cancel_at ? new Date(subscriptionPlan?.subscription.cancel_at*1000).toLocaleDateString() :''
      return (
        <>
        <Typography>Your subscription will be canceled at the end of the billing period ({endingDate}). </Typography>        
        {needMoreTime()}
        </>
      )      
    }

    if(!isPaused && (subscriptionPlan?.subscription?.status == 'active' || subscriptionPlan?.subscription?.status == 'trialing') && customerUpcomingInvoice?.total) {
          const options:any = { month: 'long', day: 'numeric', year: 'numeric' };
          const perioEndDate = new Date(customerUpcomingInvoice?.period_end * 1000);
          const dueDate = perioEndDate.toLocaleDateString('en-US', options);
          const subscriptionAmount = formatPrice(customerUpcomingInvoice?.total, customerUpcomingInvoice?.currency.toUpperCase())

          return (
            <>
              <Typography>Your next payment will be a total of {subscriptionAmount} on {dueDate}. </Typography>
              {needMoreTime()}
            </>

          )
    } else {
      return ''
    }
  }
  const invoicesList = () => {
    const validCustomerInvoices = customerInvoices?.filter((invoice: any) => invoice?.total > 0 && invoice?.number!= null);
    if( validCustomerInvoices?.length > 0 ){
      return (validCustomerInvoices.map((invoice:any) => (<Typography key={invoice.id}><a  target='_' href={invoice?.receiptUrl || invoice?.invoice_pdf}>{invoice?.number}</a></Typography>)) )
    } else {
      return <Typography >You have no receipts.</Typography>
     }
  }
  
  async function getStripeInvoice() {
    //    const requestInvoice = { email: profile.email };
    const requestInvoice = { customerId: profile.stripeCustomerId };
    return fetchBilling("getStripeInvoice", requestInvoice, getStripeInvoicesAPIUrl);
  }
  async function getStripSubscription() {
    const requestSubscription = { customerId: profile.stripeCustomerId };
    return fetchBilling("getSubscription", requestSubscription, getSubscriptionAPIUrl);
  }
  const fetchCustomerInvoices  = async() => {
    if(profile?.stripeCustomerId) {
      const customerInvoices  = await getStripeInvoice();
      const subscriptionPlan = await getStripSubscription();
      setSubscriptionPlan(subscriptionPlan);
      setPlanLoadFailed(!subscriptionPlan);
      setCustomerInvoices(customerInvoices?.invoice?.data)
      calculateInvoiceDetails(customerInvoices?.invoice?.data)
      setCustomerUpcomingInvoice(customerInvoices?.upcomingInvoice)
    }
  }
  return (
    <AuthModal open={true} onClose={handleClose}>
      <Stack>
        <Stack  sx={{paddingBottom:'30px'}}  justifyContent="space-between"   alignItems="center"  direction="row" spacing={2}>
        <img style={{height:'100%', width:'150px'}} src='/images/mattoboard-logo.jpg' />

        <IconButton sx={{width:'50px'}} component="button" onClick={(event) => handleClose(event, null)} className="close-btn">
            <CloseIcon />
        </IconButton>
        </Stack>

        <Box sx={{ maxHeight: '530px', overflowY: 'auto', paddingRight: '8px' }}>
          <Stack sx={{paddingBottom:'30px'}} direction="column" spacing={1}>
            <Typography sx={{fontWeight:'700'}}>Subscription Plan</Typography>
            {subscriptionPlanView()}
          </Stack>

          {(profile?.stripeCustomerId || isTeamWorkspace) && (
          <Stack sx={{paddingBottom:'30px'}} direction="column" spacing={1}>
            <Typography sx={{fontWeight:'700'}}>Credits</Typography>
            <Typography>Total credits: {effectiveBalance?.totalBalance ?? 0}</Typography>
            {!isPaused && customerUpcomingInvoice?.period_end && (
              <Typography>Renewal Date: {new Date(customerUpcomingInvoice.period_end * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Typography>
            )}
          </Stack>
          )}

          <Stack sx={{paddingBottom:'30px'}} direction="column" spacing={1}>
           <Typography sx={{fontWeight:'700'}}>Account and Billing</Typography>
            {nextBill()}
            {profile?.stripeSubscriptionId &&
              <Grid sx={{display: 'flex', width: '100%', justifyContent: 'right' }}>
                <ChangeBillingBtn onClick={() => window.open('https://billing.stripe.com/p/login/eVa7vVdLSdLc7gk3cc')}>Advanced Account Options</ChangeBillingBtn>
              </Grid>
            }
          </Stack>

          <Stack sx={{paddingBottom:'30px'}} direction="column" spacing={1}>
            <Typography sx={{fontWeight:'700'}}>Receipts</Typography>
            {invoicesList()}
          </Stack>

          <Stack sx={{paddingBottom:'30px'}} direction="column" spacing={1}>
              <Typography sx={{fontWeight:'700'}}>Need help? Have a question about a bill?  </Typography>
              <Typography><a  target='_' href={getContactForm(profile)}>Contact Us</a> for additional support.</Typography>
          </Stack>

          <DeleteAccountSection contactUrl={getContactForm(profile)} />
        </Box>

      </Stack>

    </AuthModal>
  )
}
